import m, {MithrilComponent, state} from '@bitstillery/mithril'
import {Button, Icon, Link, TippyJsx} from '@bitstillery/common/components/portal'
import {$t, notifier, store} from '@bitstillery/common/app'
import {cached_media_folder_files, load_media_folder_files, media_folder_file_src} from '@bitstillery/common/cms/lib/media'
import {$s} from '@/app'
import {CmsFragment} from '@/cms/runtime/fragments'

import {FooterFunFactModal} from './fun_fact/fun_fact_modal'

/** CMS media library folder holding fun-fact photos — staff can replace `fun-fact-<id>.jpg` there without a deploy. */
const FUN_FACT_FOLDER = 'fun-fact'
const FOOTER_FUN_FACT_ORDER = ['music', 'bike', 'groningen', 'market', 'stroopwafel', 'schiermonnikoog', 'landscape'] as const
type FooterFunFactId = (typeof FOOTER_FUN_FACT_ORDER)[number]

/** Null while the CMS folder hasn't loaded yet, or has no file named `fun-fact-<id>.jpg`. */
function footer_fun_fact_image_src(id: FooterFunFactId): string | null {
    const file = cached_media_folder_files(FUN_FACT_FOLDER).find((f) => f.name === `fun-fact-${id}.jpg`)
    return file ? media_folder_file_src(file) : null
}

interface FooterData {
    fact_index: number
    /** Seven clicks on the copyright mark (hover tip) toggles `$s.settings.beta`, like PanelLeft’s info icon. */
    footer_beta_clicks: number
    modal_fact_id: FooterFunFactId | null
    modal_open: boolean
}

export class Footer extends MithrilComponent {
    data: FooterData = state(
        {
            fact_index: Math.floor(Math.random() * FOOTER_FUN_FACT_ORDER.length),
            modal_fact_id: null,
            modal_open: false,
            footer_beta_clicks: 0,
        },
        'landing-footer-fun-fact',
    )

    override oninit() {
        if (cached_media_folder_files(FUN_FACT_FOLDER).length === 0) {
            void load_media_folder_files(FUN_FACT_FOLDER).then(() => m.redraw())
        }
    }

    view() {
        const address_osm_query = [process.env.MSI_ADDRESS_STREET, process.env.MSI_ADDRESS_CITY, process.env.MSI_ADDRESS_COUNTRY]
            .filter(Boolean)
            .join(', ')
        // NOTE: hand `Link` a plain (unencoded) query — `Link` runs `encodeURI` on the whole href itself.
        // Pre-encoding here (e.g. encodeURIComponent) would double-escape (`%20` → `%2520`) and break the OSM search.
        const address_osm_href =
            process.env.MSI_ADDRESS_OSM_URL || `https://www.openstreetmap.org/search?query=${address_osm_query}`

        const route = m.route.get()
        const is_cms_layout = !route.startsWith('/shop')

        const contact = $s.support.contact
        const show_footer_support_manager = Boolean($s.identity.token) && is_cms_layout && Boolean(contact?.name)

        const fact_index_wrapped =
            ((this.data.fact_index % FOOTER_FUN_FACT_ORDER.length) + FOOTER_FUN_FACT_ORDER.length) % FOOTER_FUN_FACT_ORDER.length
        const preview_fact_id = FOOTER_FUN_FACT_ORDER[fact_index_wrapped] ?? FOOTER_FUN_FACT_ORDER[0]
        const preview_image = footer_fun_fact_image_src(preview_fact_id)
        // @i18n ignore: landing.footer_fun_facts.**.{teaser,image_alt,title,body}
        const preview_teaser_key = `landing.footer_fun_facts.${preview_fact_id}.teaser`
        const preview_alt_key = `landing.footer_fun_facts.${preview_fact_id}.image_alt`
        const modal_image_src = this.data.modal_fact_id ? footer_fun_fact_image_src(this.data.modal_fact_id) : null

        return (
            <footer className='c-landing-footer'>
                <div className='landing-footer-inner'>
                    {show_footer_support_manager ? (
                        <section className='landing-footer-section landing-footer-support-manager'>
                            <div className='landing-footer-support-manager-icons'>
                                {contact?.email ? (
                                    <Button
                                        icon='email'
                                        onclick={() => {
                                            if (contact.email) {
                                                window.location.href = `mailto:${contact.email}`
                                            }
                                        }}
                                        tip={$t._('support.contact.email', {contact: contact.email})}
                                        type='info'
                                        variant='toggle'
                                    />
                                ) : null}
                                {contact?.phone ? (
                                    <Button
                                        icon='phone'
                                        onclick={() => {
                                            if (contact.phone) {
                                                window.location.href = `tel:${contact.phone}`
                                            }
                                        }}
                                        tip={$t._('support.contact.phone', {contact: contact.phone})}
                                        type='info'
                                        variant='toggle'
                                    />
                                ) : null}
                                {contact?.mobile ? (
                                    <Button
                                        icon='cellPhone'
                                        onclick={() => {
                                            if (contact.mobile) {
                                                window.location.href = `tel:${contact.mobile}`
                                            }
                                        }}
                                        tip={$t._('support.contact.mobile', {contact: contact.mobile})}
                                        type='info'
                                        variant='toggle'
                                    />
                                ) : null}
                            </div>
                            <h3 className='landing-footer-support-manager-title'>{$t('support.contact.support_manager')}</h3>
                            <div
                                className='landing-footer-support-manager-photo'
                                style={contact?.img ? {backgroundImage: `url(${contact.img})`} : undefined}
                                aria-hidden={!contact?.img}
                            />
                            <div className='landing-footer-support-manager-name'>{contact?.name}</div>
                        </section>
                    ) : null}

                    <section className='landing-footer-section landing-footer-contact'>
                        <h3 className='landing-footer-heading'>{$t('footer.contact_us')}</h3>
                        <p className='landing-footer-intro'>{$t('landing.footer_contact_intro')}</p>
                        <ul className='landing-footer-list'>
                            <li>
                                <Link href={`tel:${process.env.MSI_CONTACT_PHONE}`} target='_self'>
                                    <Icon name='phone' size='s' type='unset' />
                                    {process.env.MSI_CONTACT_PHONE}
                                </Link>
                            </li>
                            <li>
                                <Link href={`mailto:${process.env.MSI_CONTACT_EMAIL}`} target='_self'>
                                    <Icon name='email' size='s' type='unset' />
                                    {process.env.MSI_CONTACT_EMAIL}
                                </Link>
                            </li>
                            {process.env.MSI_CONTACT_WHATSAPP ? (
                                <li className='landing-footer-messaging'>
                                    <Link href={`https://wa.me/${process.env.MSI_CONTACT_WHATSAPP}`}>
                                        <span className='landing-footer-messaging-label'>WhatsApp</span>
                                        {process.env.MSI_CONTACT_WHATSAPP}
                                    </Link>
                                </li>
                            ) : null}
                            {process.env.MSI_CONTACT_WECHAT ? (
                                <li className='landing-footer-messaging'>
                                    <span>
                                        <span className='landing-footer-messaging-label'>WeChat</span>
                                        {process.env.MSI_CONTACT_WECHAT}
                                    </span>
                                </li>
                            ) : null}
                        </ul>
                    </section>

                    <section className='landing-footer-section landing-footer-company'>
                        <h3 className='landing-footer-heading'>{process.env.MSI_TITLE}</h3>
                        <address className='landing-footer-address'>
                            <Link className='landing-footer-address-link' href={address_osm_href}>
                                <Icon name='mapMarker' size='s' type='unset' />
                                <span>
                                    {process.env.MSI_ADDRESS_STREET}
                                    <br />
                                    {process.env.MSI_ADDRESS_CITY}
                                    <br />
                                    {process.env.MSI_ADDRESS_COUNTRY}
                                </span>
                            </Link>
                        </address>
                        <Link
                            className='landing-footer-contact-btn'
                            href={`mailto:${process.env.MSI_LEGAL_EMAIL}`}
                            target='_self'
                        >
                            {$t('footer.contact_us')}
                        </Link>
                        {process.env.MSI_FOLLOW_LINKEDIN ? (
                            <div className='landing-footer-follow'>
                                <h4 className='landing-footer-subheading'>{$t('footer.follow_us')}</h4>
                                <Link className='landing-footer-social' href={process.env.MSI_FOLLOW_LINKEDIN}>
                                    <Icon name='linkedin' type='unset' />
                                </Link>
                            </div>
                        ) : null}
                    </section>

                    <section className='landing-footer-section landing-footer-nav'>
                        <h3 className='landing-footer-heading'>{$t('footer.go_to')}</h3>
                        {/* The "Go to" links are the one CMS-configurable part of the footer: a
                            `cms-footer-links` fragment (of catalog <Link>s) fills the nav, falling
                            back to this hardcoded list until one is published. */}
                        <nav className='landing-footer-links'>
                            <CmsFragment
                                name='cms-footer-links'
                                fallback={[
                                    <Link href='/about-us'>
                                        {$t('breadcrumbs.about_us', {vendor: process.env.MSI_TITLE})}{' '}
                                        <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/our-team'>
                                        {$t('breadcrumbs.our_team')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/vacancies'>
                                        {$t('breadcrumbs.vacancies')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/shop'>
                                        {$t('breadcrumbs.shop')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/our-range'>
                                        {$t('breadcrumbs.our_brands')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/our-logistics-partners'>
                                        {$t('breadcrumbs.our_logistics_partners')}{' '}
                                        <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/terms-conditions'>
                                        {$t('breadcrumbs.terms_conditions')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                    <Link href='/privacy'>
                                        {$t('breadcrumbs.privacy')} <Icon name='arrowRight' size='xs' type='unset' />
                                    </Link>,
                                ]}
                            />
                        </nav>
                        {!$s.identity.token && (
                            <Button
                                className='landing-footer-cta'
                                link='/register'
                                text={$t('registration.signup')}
                                type='brand'
                            />
                        )}
                    </section>

                    <section
                        className='landing-footer-section landing-footer-fun-fact'
                        aria-label={$t('landing.footer_fun_fact_label')}
                    >
                        {/* Fun facts are CMS-configurable: a `cms-fun-facts` fragment (a <FunFacts facts={…}/>
                            with facts authored inline) fills this section, falling back to the media-folder
                            widget until one is published. */}
                        <CmsFragment
                            name='cms-fun-facts'
                            fallback={[
                                <div className='landing-footer-fun-fact-visual'>
                                    <button
                                        type='button'
                                        className='landing-footer-fun-fact-photo'
                                        style={preview_image ? {backgroundImage: `url(${preview_image})`} : undefined}
                                        aria-haspopup='dialog'
                                        aria-label={`${$t('landing.footer_fun_fact_toggle_expand')}. ${$t(preview_alt_key)}`}
                                        onclick={() => {
                                            this.data.modal_fact_id = preview_fact_id
                                            this.data.modal_open = true
                                        }}
                                    />
                                    <button
                                        type='button'
                                        className='landing-footer-fun-fact-next'
                                        aria-label={$t('landing.footer_fun_fact_next')}
                                        onclick={() => {
                                            this.data.fact_index = fact_index_wrapped + 1
                                        }}
                                    >
                                        <Icon name='chevronRight' size='s' type='unset' />
                                    </button>
                                </div>,
                                <p className='landing-footer-fun-fact-quote'>{$t(preview_teaser_key)}</p>,
                            ]}
                        />
                    </section>
                </div>
                {this.data.modal_open && this.data.modal_fact_id && modal_image_src ? (
                    <FooterFunFactModal
                        id={this.data.modal_fact_id}
                        image_src={modal_image_src}
                        onclick={() => {
                            this.data.modal_open = false
                            this.data.modal_fact_id = null
                        }}
                    />
                ) : null}
                <div className='landing-footer-copyright'>
                    <TippyJsx
                        className='landing-footer-copyright-easter'
                        content={
                            <div>
                                Made with ♥️ by <Link href='https://bitstillery.com'>Bitstillery</Link>
                                <br />
                                {`Version ${process.env.MSI_VERSION}-${process.env.BUN_ENV.slice(0, 3)} (${$s.settings.beta ? 'beta' : 'stable'})`}
                            </div>
                        }
                        delay={[2000, 200]}
                        interactive={true}
                        placement='top'
                    >
                        <span
                            className='landing-footer-copyright-mark'
                            onclick={() => {
                                this.data.footer_beta_clicks += 1
                                if (this.data.footer_beta_clicks % 7 === 0) {
                                    $s.settings.beta = !$s.settings.beta
                                    notifier.notify($t(`settings.beta.${$s.settings.beta ? 'active' : 'inactive'}`), 'info')
                                    void store.save()
                                }
                            }}
                        >
                            ©
                        </span>
                    </TippyJsx>{' '}
                    {new Date().getFullYear()} {process.env.MSI_TITLE}
                </div>
            </footer>
        )
    }
}
